import { useMemo } from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';

import { useClientData } from '@/providers/ClientData.provider';
import { Select, TextField, toBool, TransactionCode, GlCode, FieldData } from 'vopay';

import { PaymentFieldDefault } from './types';

export function useAdditionalInformation(
  GLCodes: GlCode[] | undefined,
  transactionCodes: TransactionCode[] | undefined,
  accountingSoftwareConnected: boolean,
): FieldData<PaymentFieldDefault>[] {
  const {
    response: { TransactionLabelOverrideEnabled },
  } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'UseTransactionCreationFields' });
  return useMemo(() => {
    return [
      {
        label: () => t('Fields.clientReferenceNumber.label'),
        fieldName: 'ClientReferenceNumber',
        getInputState: () => 'optional',
        showField: () => true,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField {...fieldData} variant="outlineShadow" preset="text" placeholder="" maxLength={30} />;
        },
      },
      {
        label: () => t('Fields.transactionLabel.label'),
        fieldName: 'TransactionLabel',
        getInputState: () => 'optional',
        showField: (ctx) => toBool(TransactionLabelOverrideEnabled) && ctx.PaymentMethod !== 'Interac e-Transfer',
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField {...fieldData} variant="outlineShadow" preset="text" placeholder="" maxLength={30} />;
        },
        validate: () => Yup.string().max(30, t('Fields.transactionLabel.validation')),
      },
      {
        label: () => t('Fields.transactionCode.label'),
        fieldName: 'TransactionCode',
        getInputState: () => 'optional',
        showField: () => true,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <Select
              version="2"
              {...fieldData}
              filterable
              options={(transactionCodes || []).map((code) => ({
                label: `${code.TransactionTypeCode} - ${code.Description}`,
                value: code.TransactionTypeCode,
              }))}
            />
          );
        },
      },
      {
        label: () => t('Fields.glCode.label'),
        fieldName: 'GLCode',
        getInputState: () => 'optional',
        showField: () => true,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});

          return accountingSoftwareConnected ? (
            <Select
              version="2"
              {...fieldData}
              options={(GLCodes || []).map((code: GlCode) => ({
                label: code.Name,
                value: code.GLCode,
              }))}
            />
          ) : (
            <TextField {...fieldData} variant="outlineShadow" preset="text" placeholder="" />
          );
        },
      },

      {
        label: () =>
          t('Fields.secCode.label', {
            defaultValue: 'SEC Code',
          }),
        fieldName: 'SecCode',
        getInputState: () => 'optional',
        showField: (ctx) => ctx.PaymentSelectionMethod === 'bank' && ctx.BankingPaymentRail === 'ach',
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return (
            <Select
              version="2"
              {...fieldData}
              options={[
                {
                  label: t('Fields.secCode.options.ppd', {
                    defaultValue: 'PPD - Prearranged Payment and Deposit',
                  }),
                  value: 'PPD',
                },
                {
                  label: t('Fields.secCode.options.ccd', {
                    defaultValue: 'CCD - Corporate Credit or Debit',
                  }),
                  value: 'CCD',
                },
                {
                  label: t('Fields.secCode.options.web', {
                    defaultValue: 'WEB - Internet-Initiated Entry',
                  }),
                  value: 'WEB',
                },
                {
                  label: t('Fields.secCode.options.tel', {
                    defaultValue: 'TEL - Telephone-Initiated Entry',
                  }),
                  value: 'TEL',
                },
                {
                  label: t('Fields.secCode.options.arc', {
                    defaultValue: 'ARC - Accounts Receivable Entry',
                  }),
                  value: 'ARC',
                },
                {
                  label: t('Fields.secCode.options.iat', {
                    defaultValue: 'IAT - International ACH Transaction',
                  }),
                  value: 'IAT',
                },
                {
                  label: t('Fields.secCode.options.pop', {
                    defaultValue: 'POP - Point-of-Purchase Entry',
                  }),
                  value: 'POP',
                },
              ]}
            />
          );
        },
      },
      {
        label: () => t('Fields.notes.label'),
        fieldName: 'Notes',
        getInputState: (ctx) => (ctx.values.ScheduleType === 'recurring' ? 'required' : 'optional'),
        showField: () => true,
        renderField: ({ getFormikData }) => {
          const fieldData = getFormikData<string>({});
          return <TextField {...fieldData} variant="outlineShadow" preset="text" placeholder="" maxLength={30} />;
        },
      },
    ];
  }, [GLCodes, transactionCodes, TransactionLabelOverrideEnabled, accountingSoftwareConnected]);
}
